'use client';

import { useEffect, useState } from 'react';
import * as Sentry from "@sentry/nextjs";

interface ServiceWorkerRegistrationState {
  isRegistered: boolean;
  isActive: boolean;
  scope: string | null;
  updateAvailable: boolean;
  error: string | null;
}

/**
 * Component to manually register service worker as backup for Next.js App Router
 */
export function ServiceWorkerRegister() {
  const [swState, setSwState] = useState<ServiceWorkerRegistrationState>({
    isRegistered: false,
    isActive: false,
    scope: null,
    updateAvailable: false,
    error: null,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    if ('setAppBadge' in navigator) {
      navigator.clearAppBadge().catch(() => {});
    }

    const checkServiceWorker = async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();

        if (registrations.length > 0) {
          const registration = registrations[0];

          setSwState({
            isRegistered: true,
            isActive: registration.active !== null,
            scope: registration.scope,
            updateAvailable: registration.waiting !== null,
            error: null,
          });

          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setSwState(prev => ({ ...prev, updateAvailable: true }));
                }
              });
            }
          });
        } else {
          setTimeout(async () => {
            const recheck = await navigator.serviceWorker.getRegistrations();
            if (recheck.length > 0) {
              setSwState({
                isRegistered: true,
                isActive: recheck[0].active !== null,
                scope: recheck[0].scope,
                updateAvailable: recheck[0].waiting !== null,
                error: null,
              });
            } else {
              try {
                const registration = await navigator.serviceWorker.register('/sw.js', {
                  scope: '/',
                });

                setSwState({
                  isRegistered: true,
                  isActive: registration.active !== null,
                  scope: registration.scope,
                  updateAvailable: false,
                  error: null,
                });

                await navigator.serviceWorker.ready;
                setSwState(prev => ({ ...prev, isActive: true }));
              } catch (error: any) {
                setSwState({
                  isRegistered: false,
                  isActive: false,
                  scope: null,
                  updateAvailable: false,
                  error: error.message || 'Failed to register service worker',
                });
              }
            }
          }, 2000);
        }
      } catch (error: any) {
        Sentry.captureException(error);
        setSwState(prev => ({
          ...prev,
          error: error.message || 'Unknown error',
        }));
      }
    };

    checkServiceWorker();

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      checkServiceWorker();
    });
  }, []);

  return null;
}

/**
 * Hook to get service worker registration state
 */
export function useServiceWorkerRegistration() {
  const [state, setState] = useState<ServiceWorkerRegistrationState>({
    isRegistered: false,
    isActive: false,
    scope: null,
    updateAvailable: false,
    error: null,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const updateState = async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length > 0) {
          const registration = registrations[0];
          setState({
            isRegistered: true,
            isActive: registration.active !== null,
            scope: registration.scope,
            updateAvailable: registration.waiting !== null,
            error: null,
          });
        } else {
          setState({
            isRegistered: false,
            isActive: false,
            scope: null,
            updateAvailable: false,
            error: null,
          });
        }
      } catch (error: any) {
        setState(prev => ({
          ...prev,
          error: error.message || 'Unknown error',
        }));
      }
    };

    updateState();
    const interval = setInterval(updateState, 5000);

    return () => clearInterval(interval);
  }, []);

  return state;
}
